"use client";

import React from "react";

/* ===== easing ===== */
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

/* ===== estilos embebidos (inyectados una sola vez) ===== */
const CSS = `
.pr-wrap{ position:relative; display:inline-block; box-sizing:border-box; }
.pr-svg{ display:block; width:100%; height:100%; }
.pr-center{
  position:absolute; inset:0;
  display:flex; align-items:center; justify-content:center;
  flex-direction:column; pointer-events:none;
}
.pr-title{
  font-size: clamp(.68rem, calc(12px + 0.3vw), .82rem);
  color:#64748b;
  font-weight:800;
  letter-spacing:.2px;
}
.pr-value{
  font-size: clamp(1rem, calc(18px + 0.8vw), 1.5rem);
  font-weight:900;
  letter-spacing:-.02em;
  line-height:1;
}
.pr-sub{
  margin-top:2px;
  font-size:.72rem;
  color:#94a3b8;
  font-weight:700;
}

.pr-glow::after{
  content:"";
  position:absolute; inset:-7px;
  border-radius:9999px;
  box-shadow:
    0 0 0 3px rgba(165,180,252,.22),
    0 0 28px rgba(165,180,252,.35);
  pointer-events:none;
  opacity:.55;
}
@media (max-width:640px){
  .pr-glow::after{
    inset:-5px;
    box-shadow: 0 0 0 2px rgba(165,180,252,.22), 0 0 20px rgba(165,180,252,.28);
  }
}

/* ==========================
   PULSE PRO (solo anillo)
========================== */
@keyframes prPulsePro{
  0%,100%{
    transform: translateZ(0) scale(1);
    filter: saturate(1);
  }
  50%{
    transform: translateZ(0) scale(1.02);
    filter: saturate(1.12);
  }
}

/* Pulso solo sobre el glow (si glow está activo) */
.pr-pulseNext.pr-glow::after{
  animation: prPulseGlow 1.25s ease-in-out infinite;
  opacity: .65;
}

@keyframes prPulseGlow{
  0%,100%{
    box-shadow:
      0 0 0 3px rgba(165,180,252,.18),
      0 0 26px rgba(165,180,252,.30);
    opacity: .55;
  }
  50%{
    box-shadow:
      0 0 0 7px rgba(165,180,252,.14),
      0 0 42px rgba(165,180,252,.42);
    opacity: .95;
  }
}

/* Pulso “fino” sobre el SVG (no mueve textos ni badge) */
.pr-pulseNext .pr-svg{
  animation: prPulsePro 1.25s ease-in-out infinite;
  transform-origin: 50% 50%;
}

`;
function injectStylesOnce() {
  if (typeof document === "undefined") return;
  if (document.getElementById("pr-styles")) return;
  const s = document.createElement("style");
  s.id = "pr-styles";
  s.textContent = CSS;
  document.head.appendChild(s);
}

/* ===== Helper: color por progreso (si no pasas color) ===== */
function defaultProgressColor(p) {
  const v = Number.isFinite(p) ? p : 0;
  if (v >= 85) return "#16a34a";   // green
  if (v >= 60) return "#0f5fb8";   // brand blue
  if (v >= 30) return "#f59e0b";   // amber
  return "#94a3b8";                // slate
}

/* ===== util: hex -> rgba ===== */
function hexToRgba(hex, a = 1) {
  const h = String(hex || "").replace("#", "").trim();
  if (h.length !== 6) return `rgba(15,95,184,${a})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

/* ===== Componente ===== */
export default function ProgressRing({
  value = 0,               // 0..100
  size = 140,              // px
  stroke = 12,             // px
  track = "#eef2f7",
  color,                   // si no viene, se calcula con defaultProgressColor
  start = "top",           // "top" | "right" | "bottom" | "left"
  label,                   // string opcional
  showPercent = true,      // muestra % si no hay children
  animate = true,
  duration = 700,
  glow = false,
  ariaLabel,
  className = "",
  children,
  getColor = defaultProgressColor,
  sublabel,                // opcional: texto pequeño debajo (ej. "Listo", "En curso")
  showCap = true,          // punto final
}) {
  React.useEffect(injectStylesOnce, []);

  const [display, setDisplay] = React.useState(0);
  const prev = React.useRef(0);

  React.useEffect(() => {
    const end = Number(value) || 0;
    if (!animate) {
      setDisplay(Math.round(end));
      prev.current = end;
      return;
    }
    const startV = prev.current;
    const t0 = performance.now();
    let raf;
    const step = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      setDisplay(Math.round(startV + (end - startV) * easeOutCubic(p)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    prev.current = end;
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [value, animate, duration]);

  const val = Math.max(0, Math.min(100, Number(display) || 0));

  // padding para evitar clipping
  const PAD = 2.25;
  const r = (size - stroke - PAD * 2) / 2;
  const c = 2 * Math.PI * r;

  const startAngle =
    start === "right" ? 0 :
    start === "bottom" ? 90 :
    start === "left" ? 180 : -90;

  const base = color || getColor(val);

  // gradiente sutil basado en el color base
  const gradA = hexToRgba(base, 1);
  const gradB = hexToRgba(base, 0.62);

  const dashOffset = c * (1 - val / 100);

  // “cap” (punto al final)
  const angleRad = ((startAngle + (val / 100) * 360) * Math.PI) / 180;
  const cx = size / 2;
  const cy = size / 2;
  const capX = cx + r * Math.cos(angleRad);
  const capY = cy + r * Math.sin(angleRad);

  const gid = React.useId();

  return (
    <div
      className={`pr-wrap ${glow ? "pr-glow" : ""} ${className}`}
      style={{ width: size, height: size }}
      role="img"
      aria-label={ariaLabel ?? `${label ?? "Progreso"} ${val}%`}
      title={ariaLabel ?? `${label ?? "Progreso"} ${val}%`}
    >
      <svg
        className="pr-svg"
        viewBox={`0 0 ${size} ${size}`}
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id={`prg-${gid}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={gradA} />
            <stop offset="100%" stopColor={gradB} />
          </linearGradient>
          <filter id={`prs-${gid}`} x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor={hexToRgba(base, 0.35)} />
          </filter>
        </defs>

        {/* track */}
        <circle
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke={track}
          strokeWidth={stroke}
          strokeLinecap="round"
        />

        {/* progress */}
<circle
  cx={cx} cy={cy} r={r}
  fill="none"
  stroke={`url(#prg-${gid})`}
  strokeWidth={stroke}
  strokeDasharray={c}
  strokeDashoffset={dashOffset}
  strokeLinecap="round"
  transform={`rotate(${startAngle} ${cx} ${cy})`}
  style={{ transition: `stroke-dashoffset ${duration}ms cubic-bezier(0.22, 0.61, 0.36, 1)` }}
  filter={`url(#prs-${gid})`}
/>

        {/* cap */}
{showCap && val > 0 ? (
  <circle
    cx={capX}
    cy={capY}
    r={Math.max(2.2, stroke * 0.22)}
    fill={base}
    opacity="0.95"
    filter={`url(#prs-${gid})`}
    style={{ 
      transition: `cx ${duration}ms cubic-bezier(0.22, 0.61, 0.36, 1), cy ${duration}ms cubic-bezier(0.22, 0.61, 0.36, 1)` 
    }}
  />
) : null}
      </svg>

      <div className="pr-center">
        {children ? children : (
          <>
            {label ? <span className="pr-title">{label}</span> : null}
            {showPercent ? <span className="pr-value" style={{ color: base }}>{val}%</span> : null}
            {sublabel ? <span className="pr-sub">{sublabel}</span> : null}
          </>
        )}
      </div>
    </div>
  );
}
